import {
  GatewayAssujetti,
  GatewayConformite,
  GatewayQuestionnaire,
} from "@conformite/gateway";
import { getQuestionnaireSlug } from "@src/helper/avancement.helper";
import { QuestionnaireApi } from "@src/data/questionnaire/questionnaire.api";
import { AssujettiApi } from "./assujetti.api";

const ASSUJETTI_CONTEXT_TYPE = "ASSUJETTI";

export type AssujettiIdentity = {
  name: string;
  surname: string;
  email: string | null;
  role: GatewayAssujetti.RoleAssujetti;
};

export type AssujettiQuestionnaireAxis = {
  axe: GatewayConformite.AxeConformite;
  definition: GatewayQuestionnaire.QuestionnaireDefinitionResponse;
};

export type AssujettiQuestionnairePerson = {
  identity: AssujettiIdentity;
  instanceByAxe: Partial<
    Record<
      GatewayConformite.AxeConformite,
      GatewayQuestionnaire.QuestionnaireInstanceResponse | null
    >
  >;
};

export type AssujettiQuestionnaireExportData = {
  axes: AssujettiQuestionnaireAxis[];
  persons: AssujettiQuestionnairePerson[];
};

const CONTEXT_IDS_PER_REQUEST = 500;

const chunk = <T>(items: T[], size: number): T[][] =>
  Array.from({ length: Math.ceil(items.length / size) }, (_, index) =>
    items.slice(index * size, (index + 1) * size)
  );

/**
 * Rassemble, pour un contrôle quinquennal, les questions (définitions par axe)
 * et les réponses de chaque personne assujettie (dernière instance par axe).
 */
export async function gatherAssujettiQuestionnaireExport(
  siren: string,
  millesime: number,
  availableAxes: GatewayConformite.AxeConformite[]
): Promise<AssujettiQuestionnaireExportData> {
  const axeSlugs = availableAxes
    .map((axe) => ({ axe, slug: getQuestionnaireSlug(axe) }))
    .filter(
      (
        entry
      ): entry is {
        axe: GatewayConformite.AxeConformite;
        slug: GatewayQuestionnaire.QuestionnaireSlug;
      } => Boolean(entry.slug)
    );

  const [assujettis, definitions] = await Promise.all([
    AssujettiApi.list(millesime, siren),
    Promise.all(
      axeSlugs.map(({ slug }) => QuestionnaireApi.getDefinition(slug))
    ),
  ]);

  const axes: AssujettiQuestionnaireAxis[] = axeSlugs.map(({ axe }, index) => ({
    axe,
    definition: definitions[index],
  }));
  const axeByDefinitionId = new Map(
    axes.map(({ axe, definition }) => [definition.id, axe])
  );

  const questionnaireSlugs = axeSlugs.map(({ slug }) => slug);
  const instances = await chunk(
    assujettis.map((assujetti) => assujetti.id),
    CONTEXT_IDS_PER_REQUEST
  ).reduce<Promise<GatewayQuestionnaire.QuestionnaireInstanceResponse[]>>(
    async (previous, contextIds) => [
      ...(await previous),
      ...(await QuestionnaireApi.getLatestInstances({
        contextType: ASSUJETTI_CONTEXT_TYPE,
        contextIds,
        questionnaireSlugs,
      })),
    ],
    Promise.resolve([])
  );

  const instancesByContextId = new Map<
    string,
    AssujettiQuestionnairePerson["instanceByAxe"]
  >();
  instances.forEach((instance) => {
    const axe = axeByDefinitionId.get(instance.questionnaireDefinitionId);
    if (!axe) return;
    instancesByContextId.set(instance.contextId, {
      ...instancesByContextId.get(instance.contextId),
      [axe]: instance,
    });
  });

  const persons: AssujettiQuestionnairePerson[] = assujettis.map(
    (assujetti) => ({
      identity: {
        name: assujetti.name,
        surname: assujetti.surname,
        email: assujetti.email,
        role: assujetti.role,
      },
      instanceByAxe: Object.fromEntries(
        axeSlugs.map(({ axe }) => [
          axe,
          instancesByContextId.get(assujetti.id)?.[axe] ?? null,
        ])
      ),
    })
  );

  return { axes, persons };
}
